import { supabase } from '../utils/supabase';

/**
 * Session Partnership Service
 *
 * Handles database operations for session partnerships.
 * Create path uses create_session_partnership RPC for conflict-safe inserts.
 */

export const PARTNERSHIP_RPC_CODES = {
  CREATED: 'CREATED',
  PAIR_REACTIVATED: 'PAIR_REACTIVATED',
  PAIR_ALREADY_ACTIVE: 'PAIR_ALREADY_ACTIVE',
  PLAYER_ALREADY_PARTNERED: 'PLAYER_ALREADY_PARTNERED',
  SELF_PAIR: 'SELF_PAIR',
  FORBIDDEN: 'FORBIDDEN',
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  PLAYER_NOT_ACTIVE: 'PLAYER_NOT_ACTIVE',
  SINGLES_SESSION: 'SINGLES_SESSION',
  INVALID_PLAYERS: 'INVALID_PLAYERS',
  UNIQUE_VIOLATION: 'UNIQUE_VIOLATION',
};

/**
 * Maps RPC jsonb to a consistent client result shape.
 * @param {object|null} rpcData - Raw supabase.rpc data
 * @returns {{ success: boolean, code?: string, data?: object, conflict?: object, error?: string }}
 */
export function parsePartnershipRpcResult(rpcData) {
  if (!rpcData || typeof rpcData !== 'object') {
    return { success: false, code: 'UNKNOWN', error: 'Invalid response from server' };
  }

  const code = rpcData.code || 'UNKNOWN';
  const partnership = normalizePartnershipRow(rpcData.partnership);

  if (rpcData.ok === true) {
    return { success: true, code, data: partnership };
  }

  if (code === PARTNERSHIP_RPC_CODES.PLAYER_ALREADY_PARTNERED) {
    return {
      success: false,
      code,
      error: 'One or both players are already in a partnership in this session.',
      conflict: {
        playerId: rpcData.player_id,
        existingPartnerId: rpcData.existing_partner_id,
        existingPartnershipId: rpcData.existing_partnership_id,
        partnership,
      },
    };
  }

  const messages = {
    [PARTNERSHIP_RPC_CODES.SELF_PAIR]: 'Cannot partner a player with themselves.',
    [PARTNERSHIP_RPC_CODES.FORBIDDEN]: 'You do not have permission to manage partnerships for this session.',
    [PARTNERSHIP_RPC_CODES.SESSION_NOT_FOUND]: 'Session not found.',
    [PARTNERSHIP_RPC_CODES.SINGLES_SESSION]: 'Partnerships are not used in singles sessions.',
    [PARTNERSHIP_RPC_CODES.INVALID_PLAYERS]: 'Please select both players.',
    [PARTNERSHIP_RPC_CODES.UNIQUE_VIOLATION]: 'Could not create partnership due to a conflict. Try again or remove the existing partnership first.',
    [PARTNERSHIP_RPC_CODES.PLAYER_NOT_ACTIVE]: 'Both players must be marked as playing in this session.',
  };

  return {
    success: false,
    code,
    error: messages[code] || 'Failed to create partnership. Please try again.',
  };
}

/**
 * @param {object|null} row - RPC partnership object or DB row
 * @returns {object|null}
 */
export function normalizePartnershipRow(row) {
  if (!row) return null;
  return {
    id: row.id,
    player1Id: row.player1Id ?? row.player1_id,
    player2Id: row.player2Id ?? row.player2_id,
    createdAt: row.createdAt ?? row.created_at ?? null,
  };
}

/**
 * Creates a session partnership via RPC (reactivate / conflict-aware).
 * @param {string} sessionId
 * @param {string} player1Id
 * @param {string} player2Id
 * @returns {Promise<{ success: boolean, code?: string, data?: object, conflict?: object, error?: string }>}
 */
export const createSessionPartnership = async (sessionId, player1Id, player2Id) => {
  try {
    const { data, error } = await supabase.rpc('create_session_partnership', {
      p_session_id: sessionId,
      p_player1_id: player1Id,
      p_player2_id: player2Id,
    });

    if (error) {
      console.error('[SESSION_PARTNERSHIP] RPC error:', error);
      return { success: false, code: 'RPC_ERROR', error: 'Failed to create partnership. Please try again.' };
    }

    return parsePartnershipRpcResult(data);
  } catch (error) {
    console.error('[SESSION_PARTNERSHIP] Unexpected error:', error);
    return { success: false, code: 'UNKNOWN', error: error.message || 'Failed to create partnership.' };
  }
};

/**
 * Gets all active session partnerships for a specific session
 * @param {string} sessionId - The session ID
 * @returns {Promise<Object>} - Result with active session partnerships
 */
export const getSessionPartnerships = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('session_partnerships')
      .select('*')
      .eq('session_id', sessionId)
      .is('removed_at', null)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[SESSION_PARTNERSHIP] Error fetching session partnerships:', error);
      return { success: false, error: error.message };
    }

    const partnerships = (data || []).map((sp) => normalizePartnershipRow(sp));
    return { success: true, data: partnerships };
  } catch (error) {
    console.error('[SESSION_PARTNERSHIP] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Removes a session partnership (soft delete)
 * @param {string} sessionId - The session ID
 * @param {string} player1Id - First player ID
 * @param {string} player2Id - Second player ID
 * @returns {Promise<Object>} - Result of the operation
 */
export const removeSessionPartnership = async (sessionId, player1Id, player2Id) => {
  try {
    const [player1, player2] = [player1Id, player2Id].sort();

    const { error } = await supabase
      .from('session_partnerships')
      .update({ removed_at: new Date().toISOString() })
      .eq('session_id', sessionId)
      .eq('player1_id', player1)
      .eq('player2_id', player2)
      .is('removed_at', null);

    if (error) {
      console.error('[SESSION_PARTNERSHIP] Error removing session partnership:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('[SESSION_PARTNERSHIP] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Checks if two players are in an active session partnership
 */
export const isSessionPartnership = async (sessionId, player1Id, player2Id) => {
  try {
    const [player1, player2] = [player1Id, player2Id].sort();

    const { data, error } = await supabase
      .from('session_partnerships')
      .select('id')
      .eq('session_id', sessionId)
      .eq('player1_id', player1)
      .eq('player2_id', player2)
      .is('removed_at', null)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('[SESSION_PARTNERSHIP] Error checking session partnership:', error);
      return { success: false, error: error.message };
    }

    return { success: true, exists: !!data };
  } catch (error) {
    console.error('[SESSION_PARTNERSHIP] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Gets session partnership history for analytics
 */
export const getSessionPartnershipHistory = async (sessionId) => {
  try {
    const { data, error } = await supabase
      .from('session_partnerships')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[SESSION_PARTNERSHIP] Error fetching partnership history:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('[SESSION_PARTNERSHIP] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Applies a successful create RPC result to the local partnerships list.
 * @param {object[]} partnerships - Current list
 * @param {object} newPartnership - Normalized partnership from RPC
 * @param {string} code - RPC success code
 * @returns {object[]}
 */
export function mergePartnershipIntoList(partnerships, newPartnership, code) {
  if (!newPartnership?.id) return partnerships;

  const withoutDuplicate = (partnerships || []).filter((p) => p.id !== newPartnership.id);
  const withoutPlayers = withoutDuplicate.filter(
    (p) =>
      p.player1Id !== newPartnership.player1Id &&
      p.player1Id !== newPartnership.player2Id &&
      p.player2Id !== newPartnership.player1Id &&
      p.player2Id !== newPartnership.player2Id
  );

  if (code === PARTNERSHIP_RPC_CODES.PAIR_ALREADY_ACTIVE) {
    const exists = (partnerships || []).some((p) => p.id === newPartnership.id);
    return exists ? partnerships : [...withoutPlayers, newPartnership];
  }

  return [...withoutPlayers, newPartnership];
}

const PARTNERSHIP_FLUSH_OK_CODES = new Set([
  PARTNERSHIP_RPC_CODES.CREATED,
  PARTNERSHIP_RPC_CODES.PAIR_REACTIVATED,
  PARTNERSHIP_RPC_CODES.PAIR_ALREADY_ACTIVE,
]);

export async function flushPartnershipAddFromOfflineCommand(command, { sessionId }) {
  if (!sessionId || !command?.player1Id || !command?.player2Id) {
    throw new Error('Missing partnership players');
  }
  const result = await createSessionPartnership(sessionId, command.player1Id, command.player2Id);
  if (result.success || PARTNERSHIP_FLUSH_OK_CODES.has(result.code)) {
    return result;
  }
  const error = new Error(result.error || 'Failed to create partnership');
  error.code = result.code;
  throw error;
}

export async function flushPartnershipRemoveFromOfflineCommand(command, { sessionId }) {
  if (!sessionId || !command?.player1Id || !command?.player2Id) {
    throw new Error('Missing partnership players');
  }
  const result = await removeSessionPartnership(sessionId, command.player1Id, command.player2Id);
  if (result.success) return result;
  throw new Error(result.error || 'Failed to remove partnership');
}
